import axios from 'axios';
import { getApiBase } from '@/config/api';
import { getToken } from '@/utils/tokenStorage';

/**
 * Verifica se o navegador suporta notificações push
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Verifica se o app está instalado como PWA
 */
export function isInstalledPWA() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
}

/**
 * Solicita permissão para notificações
 * @returns {Promise<string>} 'granted', 'denied' ou 'default'
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    console.warn('[Notifications] Navegador não suporta notificações');
    return 'denied';
  }

  const permission = await Notification.requestPermission();
  console.log('[Notifications] Permissão:', permission);
  return permission;
}

/**
 * Registra o dispositivo para receber push notifications
 * Envia a subscription para o backend
 */
export async function subscribeToPush() {
  try {
    if (!isPushSupported()) {
      console.warn('[Push] Push não suportado neste navegador');
      return null;
    }

    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.warn('[Push] Permissão negada');
      return null;
    }

    const registration = await navigator.serviceWorker.ready;

    // Buscar VAPID public key do backend
    const apiBase = getApiBase();
    const { data } = await axios.get(`${apiBase}/push/vapid-public-key`);
    const vapidPublicKey = data.public_key;

    if (!vapidPublicKey) {
      console.warn('[Push] VAPID public key não configurada no servidor');
      return null;
    }

    // Converter VAPID key para Uint8Array
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

    // Subscrever
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey
    });

    // Enviar subscription para o backend
    const token = getToken();
    await axios.post(`${apiBase}/push/subscribe`, {
      ...subscription.toJSON(),
      tipo: 'staff'
    }, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });

    console.log('[Push] Inscrito com sucesso (staff)');
    return subscription;
  } catch (error) {
    console.error('[Push] Erro ao inscrever:', error);
    return null;
  }
}

/**
 * Inscreve o dispositivo do CLIENTE para receber notificações de um pedido específico.
 * Usado no CardapioPublico após o cliente fazer um pedido (igual iFood).
 * @param {string} pedidoId - ID do pedido para acompanhar
 * @returns {Promise<PushSubscription|null>}
 */
export async function subscribeToOrderPush(pedidoId) {
  try {
    if (!isPushSupported()) {
      console.warn('[Push] Push não suportado neste navegador');
      return null;
    }

    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.warn('[Push] Permissão negada pelo cliente');
      return null;
    }

    const registration = await navigator.serviceWorker.ready;

    const apiBase = getApiBase();
    const { data } = await axios.get(`${apiBase}/push/vapid-public-key`);
    const vapidPublicKey = data.public_key;

    if (!vapidPublicKey) {
      console.warn('[Push] VAPID public key não configurada');
      return null;
    }

    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

    // Reusar subscription existente ou criar nova
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });
    }

    // Enviar para o backend com tipo "cliente" e pedido_id
    await axios.post(`${apiBase}/push/subscribe`, {
      ...subscription.toJSON(),
      tipo: 'cliente',
      pedido_id: pedidoId
    }, {
      headers: { 'Content-Type': 'application/json' }
    });

    console.log(`[Push] Cliente inscrito para pedido ${pedidoId}`);
    return subscription;
  } catch (error) {
    console.error('[Push] Erro ao inscrever cliente:', error);
    return null;
  }
}

/**
 * Remove a inscrição de push notifications
 */
export async function unsubscribeFromPush() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      const apiBase = getApiBase();
      const token = getToken();

      await axios.post(`${apiBase}/push/unsubscribe`, {
        endpoint: subscription.endpoint
      }, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      await subscription.unsubscribe();
      console.log('[Push] Desinscrição realizada');
    }
  } catch (error) {
    console.error('[Push] Erro ao desinscrever:', error);
  }
}

/**
 * Envia uma notificação local (sem push, direto no navegador)
 */
export function showLocalNotification(title, options = {}) {
  if (Notification.permission !== 'granted') return;

  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.showNotification(title, {
        icon: '/logo192.png',
        badge: '/logo192.png',
        vibrate: [200, 100, 200],
        ...options
      });
    });
  } else {
    new Notification(title, {
      icon: '/logo192.png',
      ...options
    });
  }
}

/**
 * Converte uma string base64 URL-safe para Uint8Array (necessário para VAPID)
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
